import { useState } from "react";
import Lightbox from "./Lightbox";

interface ProductImagesProps {
  images: string[];
  name: string;
}

export default function ProductImages({ images, name }: ProductImagesProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [zoomed, setZoomed] = useState(false);
  const [zoomPos, setZoomPos] = useState({ x: 50, y: 50 });
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setZoomPos({ x, y });
  };

  return (
    <>
      <div className="flex gap-4 w-full h-full">
        {/* Thumbnails - left column */}
        <div className="flex flex-col gap-2.5 w-16 shrink-0">
          {images.map((img, i) => (
            <button
              key={i}
              onClick={() => setActiveIndex(i)}
              className={`w-16 h-20 overflow-hidden border-2 transition-all cursor-pointer shrink-0 ${
                activeIndex === i ? "border-midnight" : "border-transparent"
              }`}
            >
              <img
                src={img}
                alt={`${name} view ${i + 1}`}
                className="w-full h-full object-cover object-top"
              />
            </button>
          ))}
        </div>

        {/* Main Image */}
        <div className="flex-1 relative overflow-hidden bg-pearl/10 group/mainimg">
          <div
            className="w-full h-full cursor-zoom-in"
            style={{ aspectRatio: "3/4" }}
            onMouseEnter={() => setZoomed(true)}
            onMouseLeave={() => setZoomed(false)}
            onMouseMove={handleMouseMove}
            onClick={() => setLightboxOpen(true)}
          >
            <img
              src={images[activeIndex]}
              alt={name}
              className="w-full h-full object-cover object-top transition-opacity duration-400"
              style={
                zoomed
                  ? {
                      transformOrigin: `${zoomPos.x}% ${zoomPos.y}%`,
                      transform: "scale(1.8)",
                      transition: "transform 0.1s ease-out",
                    }
                  : { transform: "scale(1)", transition: "transform 0.3s ease-out" }
              }
            />
          </div>

          {/* Expand hint — appears on hover */}
          <div className="absolute top-3 right-3 opacity-0 group-hover/mainimg:opacity-100 transition-opacity duration-200 pointer-events-none">
            <div className="w-8 h-8 flex items-center justify-center bg-moonlight/80 backdrop-blur-sm text-midnight/60">
              <i className="ri-fullscreen-line text-sm" />
            </div>
          </div>

          {/* Arrow Navigation */}
          {images.length > 1 && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
                }}
                className="absolute left-3 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center bg-moonlight/80 backdrop-blur-sm text-midnight hover:bg-moonlight transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-left-s-line text-lg" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
                }}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center bg-moonlight/80 backdrop-blur-sm text-midnight hover:bg-moonlight transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-right-s-line text-lg" />
              </button>
            </>
          )}

          {/* Image Counter */}
          <div className="absolute bottom-3 right-3 text-[10px] tracking-widest bg-moonlight/70 backdrop-blur-sm px-2 py-1 text-midnight/60">
            {activeIndex + 1} / {images.length}
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {lightboxOpen && (
        <Lightbox
          images={images}
          name={name}
          initialIndex={activeIndex}
          onClose={() => setLightboxOpen(false)}
        />
      )}
    </>
  );
}
